// =============================================================
// VARIANCE — The Executive Command Center
// File      : Settings.tsx
// Location  : /home/Applications/variance/web/src/pages/Settings.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium dark-glass Settings page with categorized tabs.
// =============================================================

import { useState, useEffect } from 'react';
import { LayoutDashboard, Monitor, AlignLeft, Check, Palette, HelpCircle } from 'lucide-react';
import { applyTheme, getSavedTheme, getSavedShell, saveShell, Theme, ShellType } from '../lib/theme';
import { useHelp } from '../components/HelpSystem';

export function Settings() {
  // --- TABS STATE ---
  const [activeTab, setActiveTab] = useState<'appearance' | 'guidance'>('appearance');

  // --- EXISTING UI STATE ---
  const [shell, setShell] = useState<ShellType>('shell-classic');
  const [theme, setTheme] = useState<Theme>('theme-dark');

  // --- HELP STATE ---
  const { isHelpEnabled, toggleHelp } = useHelp();

  useEffect(() => {
    setShell(getSavedShell());
    setTheme(getSavedTheme());
  }, []);

  const handleShellChange = (newShell: ShellType) => {
    setShell(newShell);
    saveShell(newShell);
    window.dispatchEvent(new Event('shellChanged'));
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  return (
    <div className="space-y-8 max-w-6xl">
      
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Settings</h1>
        <p className="text-sm mt-2 tracking-wide" style={{ color: 'var(--text-secondary)' }}>Manage your preferences and application behavior.</p>
      </div>

      {/* TABS NAVIGATION */}
      <div className="flex border-b space-x-8" style={{ borderColor: 'var(--border-subtle)' }}>
        <button 
          onClick={() => setActiveTab('appearance')} 
          className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors ${activeTab === 'appearance' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
        >
          <Palette size={18} />
          Appearance
        </button>
        <button 
          onClick={() => setActiveTab('guidance')} 
          className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors ${activeTab === 'guidance' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
        >
          <HelpCircle size={18} />
          Guidance
        </button>
      </div>

      {/* TAB 1: APPEARANCE (YOUR EXISTING UI) */}
      {activeTab === 'appearance' && (
        <div className="space-y-10 animate-in fade-in duration-300">
          
          {/* Layout Selection */}
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)' }}>
            <h2 className="text-lg font-semibold mb-6 tracking-wide" style={{ color: 'var(--text-primary)' }}>Canvas Layout</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              <button onClick={() => handleShellChange('shell-classic')} className={`group relative p-6 rounded-xl flex flex-col items-center gap-4 transition-all duration-300 border-2 ${shell === 'shell-classic' ? 'border-[var(--brand-accent)] shadow-[0_0_20px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                {shell === 'shell-classic' && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: 'var(--brand-accent)' }}><Check size={12} color="white" /></div>}
                <LayoutDashboard size={40} className="transition-transform duration-300 group-hover:scale-110" style={{ color: shell === 'shell-classic' ? 'var(--brand-accent)' : 'var(--text-secondary)' }} />
                <div className="text-center">
                  <span className="block font-bold text-base mb-1" style={{ color: 'var(--text-primary)' }}>Classic Command</span>
                  <span className="block text-xs" style={{ color: 'var(--text-secondary)' }}>Fixed left sidebar. High density.</span>
                </div>
              </button>

              <button onClick={() => handleShellChange('shell-immersive')} className={`group relative p-6 rounded-xl flex flex-col items-center gap-4 transition-all duration-300 border-2 ${shell === 'shell-immersive' ? 'border-[var(--brand-accent)] shadow-[0_0_20px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                {shell === 'shell-immersive' && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: 'var(--brand-accent)' }}><Check size={12} color="white" /></div>}
                <AlignLeft size={40} className="transition-transform duration-300 group-hover:scale-110" style={{ color: shell === 'shell-immersive' ? 'var(--brand-accent)' : 'var(--text-secondary)' }} />
                <div className="text-center">
                  <span className="block font-bold text-base mb-1" style={{ color: 'var(--text-primary)' }}>Immersive Canvas</span>
                  <span className="block text-xs" style={{ color: 'var(--text-secondary)' }}>Icon-only rail. Maximum space.</span>
                </div>
              </button>

              <button onClick={() => handleShellChange('shell-topnav')} className={`group relative p-6 rounded-xl flex flex-col items-center gap-4 transition-all duration-300 border-2 ${shell === 'shell-topnav' ? 'border-[var(--brand-accent)] shadow-[0_0_20px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                {shell === 'shell-topnav' && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: 'var(--brand-accent)' }}><Check size={12} color="white" /></div>}
                <Monitor size={40} className="transition-transform duration-300 group-hover:scale-110" style={{ color: shell === 'shell-topnav' ? 'var(--brand-accent)' : 'var(--text-secondary)' }} />
                <div className="text-center">
                  <span className="block font-bold text-base mb-1" style={{ color: 'var(--text-primary)' }}>Top-Nav Executive</span>
                  <span className="block text-xs" style={{ color: 'var(--text-secondary)' }}>Horizontal navigation. Clean.</span>
                </div>
              </button>

            </div>
          </div>

          {/* Theme Selection */}
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)' }}>
            <h2 className="text-lg font-semibold mb-6 tracking-wide" style={{ color: 'var(--text-primary)' }}>Color Theme</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              
              <button onClick={() => handleThemeChange('theme-dark')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-dark' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#050505] border border-[#333333] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-dark' && <Check size={16} color="#DC2626" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Exec Dark</span>
              </button>

              <button onClick={() => handleThemeChange('theme-corporate')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-corporate' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#0A192F] border border-[#2B4C7E] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-corporate' && <Check size={16} color="#3B82F6" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Corporate</span>
              </button>

              <button onClick={() => handleThemeChange('theme-light')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-light' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-light' && <Check size={16} color="#DC2626" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Light</span>
              </button>

              <button onClick={() => handleThemeChange('theme-slate')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-slate' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#334155] border border-[#94A3B8] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-slate' && <Check size={16} color="#14B8A6" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Slate</span>
              </button>

              <button onClick={() => handleThemeChange('theme-contrast')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-contrast' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-black border border-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-contrast' && <Check size={16} color="#FACC15" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Contrast</span>
              </button>

              <button onClick={() => handleThemeChange('theme-purple')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-purple' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#1E1B4B] border border-[#6D28D9] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-purple' && <Check size={16} color="#8B5CF6" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Purple</span>
              </button>

              <button onClick={() => handleThemeChange('theme-forest')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-forest' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#022C22] border border-[#047857] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-forest' && <Check size={16} color="#10B981" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Forest</span>
              </button>

              <button onClick={() => handleThemeChange('theme-sunset')} className={`group p-3 rounded-xl flex flex-col items-center gap-3 transition-all duration-300 border-2 ${theme === 'theme-sunset' ? 'border-[var(--brand-accent)] shadow-[0_0_15px_var(--brand-accent)]' : 'border-[var(--border-subtle)] hover:border-gray-500'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <div className="w-10 h-10 rounded-lg bg-[#29120B] border border-[#7C3A1E] flex items-center justify-center group-hover:scale-110 transition-transform">
                  {theme === 'theme-sunset' && <Check size={16} color="#F97316" />}
                </div>
                <span className="text-[10px] font-bold tracking-wider uppercase text-center" style={{ color: 'var(--text-primary)' }}>Sunset</span>
              </button>

            </div>
          </div>
        </div>
      )}

      {/* TAB 2: GUIDANCE */}
      {activeTab === 'guidance' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="p-8 rounded-2xl flex items-center justify-between" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)' }}>
            <div>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Contextual Help System</h2>
              <p className="text-sm max-w-xl" style={{ color: 'var(--text-secondary)' }}>
                When enabled, pages will display explanatory banners, and forms will show field-level tooltips (?) to guide you through complex data entry like Master vs. Transaction records.
              </p>
            </div>
            <button 
              onClick={toggleHelp}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${isHelpEnabled ? 'bg-[var(--brand-accent)]' : 'bg-gray-600'}`}
            >
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${isHelpEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
